import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { css } from '@bts-web/utils-style-engine';
import { getAppConfig } from '@bts-web/core-app-config';
import { AssetType, NonNullableStakeTransactionData } from './reusableTypes';
import { getStakeTranslations } from '../../staking/utils/getStakeTranslations';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { StakingTransactionStatus } from '@bts-web/data-layer/server';
import {
  getRoundedPrecision,
  intlAssetValueFormatting,
  intlFiatValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import { LocalizedDate } from '../../common/components';
import {
  transactionTypeByTypeName,
  TransactionTypes,
} from '../../transactions/types/transactions';
import { RegularDismissCircle } from '../../common/svgIcons';
import { isBpfsAsset } from './isBpfsAsset';

export type StakeMessage = {
  title: string;
  statusTitleIcon?: ReactNode;
  subtitle?: ReactNode;
  poweredByText?: string;
  statusContent?: ReactNode;
  secondaryButton: {
    text: string;
    url: string;
  };
  primaryButton: {
    text: string;
    url: string;
    replace?: boolean;
  };
};

export function getStakeMessages({
  translationUtil,
  locale,
  transactionData,
  isUnstaking,
}: {
  translationUtil: TFunction;
  locale: string;
  transactionData: NonNullableStakeTransactionData;
  isUnstaking: boolean;
}): Record<NonNullable<StakingTransactionStatus>, StakeMessage> {
  const { currency } = getAppConfig();

  const translations = getStakeTranslations(translationUtil);

  const { assetAmount, asset, fiatAmount } = transactionData;

  const isBpf = isBpfsAsset(asset?.__typename as AssetType);

  const transactionType: TransactionTypes =
    transactionTypeByTypeName[
      transactionData?.__typename as keyof typeof transactionTypeByTypeName
    ];

  const title = isUnstaking
    ? translationUtil('unstake_success_title', {
        amount: intlAssetValueFormatting(
          Number(getRoundedPrecision(assetAmount)),
          {
            locale,
            fractionDigits:
              assetAmount?.precision ?? MaximumFractionDigits.ASSET,
          },
        ),
        symbol: asset?.symbol,
      })
    : translationUtil('stake_success_title', {
        assetAmount: `${intlAssetValueFormatting(
          Number(getRoundedPrecision(assetAmount)),
          {
            locale,
            fractionDigits:
              assetAmount?.precision ?? MaximumFractionDigits.ASSET,
          },
        )} ${asset?.symbol}`,
      });

  return {
    SUCCESS: {
      title: title,
      statusContent: transactionData?.createdAt ? (
        <LocalizedDate
          translationKey={
            isUnstaking
              ? 'unstake_success_subtitle'
              : 'stake_success_subtitle_@tenant'
          }
          date={transactionData?.createdAt}
          locale={locale}
        />
      ) : null,

      subtitle: fiatAmount?.value
        ? `(≈${intlFiatValueFormatting(getRoundedPrecision(fiatAmount), {
            locale,
            currency,
            fractionDigits: fiatAmount?.precision,
          })})`
        : null,
      poweredByText: isBpf ? translations.poweredByBpfs : undefined,
      primaryButton: {
        text: translations.okGotItButton,
        url: `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', transactionData.asset.id)}?ref=portfolio`,
        replace: true,
      },
      secondaryButton: {
        text: translations.viewDetails,
        url: `${NEXT_ROUTES.TRANSACTIONS_DETAILS.replace(
          '[id]',
          transactionData.id,
        )}?transactionType=${transactionType}`,
      },
    },
    FAILED: {
      title: isUnstaking
        ? translations.unstakeFailedTitle
        : translations.stakeFailedTitle,
      statusTitleIcon: (
        <div
          className={css({
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '100%',
            p: {
              base: 'extra_small_2',
              lg: 'small',
            },
            backgroundColor: 'negative.fill_focused',
            color: 'negative.text_primary',
          })}
        >
          <RegularDismissCircle size={48} color="negative.text_primary" />
        </div>
      ),
      subtitle: isUnstaking
        ? translations.unstakeFailedSubtitle
        : translations.stakeFailedSubtitle,
      primaryButton: {
        text: translations.tryAgain,
        url: `${NEXT_ROUTES.ASSET_DETAILS.replace(
          '[id]',
          transactionData.asset.id,
        )}/${isUnstaking ? 'unstake' : 'stake'}`,
      },
      secondaryButton: {
        text: translations.okGoToPortfolio,
        url: `${NEXT_ROUTES.PORTFOLIO}`,
      },
    },
    PENDING: {
      title: '',
      subtitle: '',
      poweredByText: isBpf ? translations.poweredByBpfs : undefined,
      primaryButton: {
        text: translations.okGotItButton,
        url: `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', transactionData.asset.id)}?ref=portfolio`,
        replace: true,
      },
      secondaryButton: {
        text: translations.viewDetails,
        url: `${NEXT_ROUTES.TRANSACTIONS_DETAILS.replace(
          '[id]',
          transactionData.id,
        )}?transactionType=${transactionType}`,
      },
    },
  };
}
